import { CommonModule } from '@angular/common'
import { Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core'
import { StringMap } from '@naturalcycles/js-lib'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { getMatchingTranslationKey, recInspect } from './inspector.util.js'

enum NotificationType {
  Console = 0,
  Alert = 1,
}

@Component({
  selector: 'app-inspector',
  imports: [CommonModule],
  templateUrl: './inspector.component.html',
  styleUrls: ['./inspector.component.scss'],
})
export class InspectorComponent implements OnInit, OnDestroy {
  private ngZone = inject(NgZone)
  private translateService = inject(TranslateService)
  private sub?: Subscription
  private translations: StringMap = {}

  protected inspectorText = ''

  protected handleClick(): void {
    // We have messed with the DOM, so easiest to just reload
    location.reload()
  }

  private searchForTextAndNotify = (text: string, type: NotificationType): void => {
    const match = getMatchingTranslationKey(text || '', this.translations)
    if (text && match) {
      const fn = type === NotificationType.Console ? console.log : alert
      fn(`Found translation for text:\n${text}\n\n${match.replaceAll(',', '')}`)
    }
  }

  /**
   * A handy tool in case we don't catch the text we want to inspect
   * The user can highlight text and we'll log the translation key
   * This is because elements like this:
   * ```html
   * <span>
   *   <img src="..." />
   *   Some text // Raw text, textContent of the span
   * </span>
   * ```
   * will only highlight the image, not the text
   */
  private selectListener = (): void => {
    const text = window.getSelection()?.toString().trim()
    if (text) {
      this.searchForTextAndNotify(text, NotificationType.Alert)
      window.getSelection()?.removeAllRanges()
    }
  }

  private mouseOverListener = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const qaUrl = target.getAttribute('__inspector_img_url')
    const translateKey = target.getAttribute('__inspector_translate_key')
    if (qaUrl) {
      this.inspectorText = `Image url: ${qaUrl}`
    } else if (translateKey) {
      this.inspectorText = `Translation key(s): ${translateKey}`
    } else {
      this.inspectorText = ''
    }
  }

  ngOnInit(): void {
    this.translations = (this.translateService.translations[this.translateService.currentLang] ||
      {}) as StringMap
    ;(window as any).getKey = (text: string): void =>
      this.searchForTextAndNotify(text, NotificationType.Console)

    this.sub = this.ngZone.onStable.subscribe(() => {
      recInspect(document.body, this.translations)
    })

    document.addEventListener('pointerup', this.selectListener)
    document.addEventListener('mouseover', this.mouseOverListener)
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe()
    document.removeEventListener('pointerup', this.selectListener)
    document.removeEventListener('mouseover', this.mouseOverListener)
    ;(window as any).getKey = undefined
  }
}
